import React, { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { FileChip } from './FileChip';
import { cx, focusRing, motion } from './classes';

/**
 * An older version's analysis, collapsed to one line above the latest one: its file, score, a short
 * verdict ("Needs work"), its main issue, and a recap when opened. Old scores get no amber; progress
 * color belongs to the latest version.
 */
export const VersionCard = ({ version, filename, score, verdict, mainIssue, recap, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();

  return (
    <div className="flex flex-col gap-space-2">
      {filename && <FileChip filename={filename} version={version} quiet className="self-end" />}
      <div className="rounded-radius-lg border border-line">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen(!open)}
          className={cx('w-full min-h-[64px] px-space-5 py-space-3 flex items-center gap-space-4 text-left rounded-radius-lg bg-transparent', focusRing)}
        >
          <span
            aria-hidden="true"
            className="w-target-min h-target-min flex-none rounded-full border-4 border-line inline-flex items-center justify-center font-display font-extrabold text-[18px] leading-none text-ink"
          >
            {score ?? '–'}
          </span>
          <span className="flex-1 min-w-0 flex flex-col gap-0.5 font-text">
            <span className="text-body font-bold text-ink">
              V{version} analysis{verdict ? ` · ${verdict}` : ''}
              <span className="sr-only">{score != null ? `, scored ${score} out of 10` : ', not scored'}</span>
            </span>
            {mainIssue && <span className="text-small text-muted">{mainIssue}</span>}
          </span>
          <ChevronDown
            size={18}
            strokeWidth={2.5}
            aria-hidden="true"
            className={cx('flex-none text-muted transition-transform', motion, !open && '-rotate-90')}
          />
        </button>
        <div id={panelId} hidden={!open} className={cx(open && 'flex', 'flex-col gap-space-2 px-space-5 pb-space-5 sm:pl-[80px] font-text')}>
          {recap && <p className="m-0 text-small text-ink-soft">{recap}</p>}
          <span className="text-small text-muted">This is an older version. Your latest analysis is below.</span>
        </div>
      </div>
    </div>
  );
};
